// Web Push registration from the browser side.
//
// Three things make this awkward and none of them are avoidable:
//
//   1. Push requires a service worker, which requires a secure context. That means
//      HTTPS or localhost — so it silently does nothing on a plain-HTTP LAN
//      address, which is exactly how someone tests on a phone.
//   2. `Notification.requestPermission()` must be called from a user gesture, or
//      browsers ignore it (and Safari rejects it). So permission is never asked
//      for on page load — only from an explicit button.
//   3. The VAPID public key must reach `subscribe()` as a Uint8Array, not the
//      base64url string the server sends.
//
// Every failure path here returns a reason rather than throwing. Push is optional,
// and a browser that cannot do it should produce an explanation on the prefs
// screen, not an error.

import { removePushSubscription, savePushSubscription } from './api/client.js';

export type PushState =
  | { supported: false; reason: string }
  | { supported: true; permission: NotificationPermission; subscribed: boolean };

/** Why push is unavailable, or null when it is available. */
function unsupportedReason(): string | null {
  if (!('serviceWorker' in navigator)) return 'This browser does not support service workers.';
  if (!('PushManager' in window)) return 'This browser does not support push notifications.';
  if (!('Notification' in window)) return 'This browser does not support notifications.';
  // isSecureContext covers HTTPS and localhost. Worth naming explicitly: testing
  // on a phone over a http://192.168.x.x address fails here and nowhere else.
  if (!window.isSecureContext) {
    return 'Push notifications need a secure connection (HTTPS).';
  }
  return null;
}

export async function pushState(): Promise<PushState> {
  const reason = unsupportedReason();
  if (reason) return { supported: false, reason };

  try {
    const registration = await navigator.serviceWorker.getRegistration();
    const existing = registration ? await registration.pushManager.getSubscription() : null;
    // SELF-HEALING (2026-10-20). The browser can hold a subscription the server
    // has no row for (a save that failed, a row deleted by a 404/410 on some past
    // send, a database restore), and then this page says "On for this device"
    // while nothing is ever sent. Found when a test member's Edge showed ON with
    // zero rows in push_subscriptions. The save is an idempotent upsert on the
    // endpoint, so re-asserting it on every read is harmless, and only does
    // anything for a signed-in user who really holds a subscription.
    if (existing && Notification.permission === 'granted') {
      const keys = extractKeys(existing);
      if (keys) {
        void savePushSubscription({ endpoint: existing.endpoint, ...keys }).catch(() => undefined);
      }
    }
    return {
      supported: true,
      permission: Notification.permission,
      subscribed: existing !== null,
    };
  } catch {
    return { supported: false, reason: 'Could not read the notification state.' };
  }
}

/**
 * Register the service worker, ask permission, subscribe, and tell the server.
 *
 * Must be called from a user gesture. Returns null on success, or a message to
 * show. `vapidPublicKey` comes from the server — an empty one means push is not
 * configured there, and the caller should not offer the button at all.
 */
export async function enablePush(vapidPublicKey: string): Promise<string | null> {
  const reason = unsupportedReason();
  if (reason) return reason;
  if (!vapidPublicKey) return 'Push notifications are not available on this server.';

  let registration: ServiceWorkerRegistration;
  try {
    registration = await navigator.serviceWorker.register('/sw.js');
    // Wait for it to be usable: subscribing against a still-installing worker
    // fails intermittently, which is the worst kind of bug to chase.
    await navigator.serviceWorker.ready;
  } catch (err) {
    return `Could not register the service worker: ${String(err)}`;
  }

  const permission = await Notification.requestPermission();
  if (permission === 'denied') {
    // Once denied, the browser will not ask again — only site settings can undo
    // it, so say that rather than inviting a pointless retry.
    return 'Notifications are blocked for this site. Allow them in your browser settings, then try again.';
  }
  if (permission !== 'granted') return 'Notification permission was not granted.';

  let subscription: PushSubscription;
  try {
    subscription = await registration.pushManager.subscribe({
      // Required by every current browser: a push that shows nothing is not
      // allowed, and passing false makes subscribe() reject.
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
    });
  } catch (err) {
    return `Could not subscribe to push: ${String(err)}`;
  }

  const keys = extractKeys(subscription);
  if (!keys) return 'The browser returned an unusable subscription.';

  try {
    await savePushSubscription({ endpoint: subscription.endpoint, ...keys });
  } catch (err) {
    // The browser now holds a subscription the server does not know about. Undo
    // it, so the UI and reality agree rather than showing "on" for a device that
    // will never receive anything.
    await subscription.unsubscribe().catch(() => undefined);
    return err instanceof Error ? err.message : 'Could not save the subscription.';
  }

  return null;
}

/** Unsubscribe this device, both in the browser and on the server. */
export async function disablePush(): Promise<string | null> {
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    const subscription = registration ? await registration.pushManager.getSubscription() : null;
    if (!subscription) return null;

    const { endpoint } = subscription;
    await subscription.unsubscribe();
    // Server-side removal happens after the browser's, so a failure here leaves a
    // stale row that the next push will clean up on its 410 rather than leaving
    // the browser subscribed to something nobody sends to.
    await removePushSubscription(endpoint);
    return null;
  } catch (err) {
    return err instanceof Error ? err.message : 'Could not turn off notifications.';
  }
}

/** The two keys the server needs, base64url-encoded. */
function extractKeys(sub: PushSubscription): { p256dh: string; auth: string } | null {
  const p256dh = sub.getKey('p256dh');
  const auth = sub.getKey('auth');
  if (!p256dh || !auth) return null;
  return { p256dh: base64Url(p256dh), auth: base64Url(auth) };
}

function base64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * base64url string to Uint8Array, for `applicationServerKey`.
 *
 * The padding restore and the `-_` to `+/` swap are both required: atob rejects
 * base64url as-is, and a key that decodes to the wrong bytes makes subscribe()
 * fail with an opaque error.
 */
function urlBase64ToUint8Array(base64Url: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64Url.length % 4)) % 4);
  const base64 = (base64Url + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  // Backed by an explicit ArrayBuffer, because `applicationServerKey` wants a
  // BufferSource over ArrayBuffer specifically — a plain Uint8Array is typed as
  // ArrayBufferLike, which admits SharedArrayBuffer and so does not match.
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}
